import { config, isReproSatelliteNode } from './config.js';

/**
 * Standard Pedigree Nomenclature (Bennett et al.) — a render-time labelling of
 * the pedigree DERIVED from the current layout:
 *   - generations are Roman numerals, I = topmost;
 *   - individuals are numbered 1..n left-to-right within their generation.
 *
 * These numbers are a SNAPSHOT of the displayed arrangement: they are recomputed
 * whenever the layout changes (move / add / remove) and are NOT persisted and
 * NOT the UPN. This module is pure and deterministic.
 */

// Standard subtractive Roman numerals for 1..3999 (the topmost present
// generation is I). Returns '' for non-integers or out-of-range input — a
// defensive guard; real pedigrees never approach the bound.
export function toRoman(n) {
  if (!Number.isInteger(n) || n < 1 || n > 3999) return '';
  const table = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I']
  ];
  let out = '';
  let rem = n;
  for (const [v, s] of table) {
    while (rem >= v) {
      out += s;
      rem -= v;
    }
  }
  return out;
}

// Canonical total order over pids that works whether pids are numbers or
// strings (the data model tolerates non-numeric pids). Mirrors layoutV2's
// cmpPid intentionally so left-to-right numbering is a stable, deterministic
// tiebreak when two nodes share an x.
function cmpPid(a, b) {
  if (a === b) return 0;
  const an = typeof a === 'number';
  const bn = typeof b === 'number';
  if (an && bn) return a - b;
  if (an) return -1; // numbers sort before strings
  if (bn) return 1;
  return a < b ? -1 : 1;
}

// Which person nodes receive a number. Confirmed product decision (TGCLOUD-1379):
// number every DRAWN individual — liveborn, deceased, proband, AND pregnancy
// losses / fetuses (isFetus) and de-identified people (isHide: symbol drawn,
// only its text labels blanked). Exclude only nodes that aren't a distinct drawn
// individual: non-person markers (UNION), fully hidden, soft-deleted, and proxy
// duplicates (a proxy is a synthetic second rendering of an already-numbered
// person — numbering it would count the same individual twice).
// Surfaced as assignNomenclature's `includePredicate` so the policy is a one-line
// override if product revisits the fetus/loss treatment.
export function defaultIncludePredicate(node) {
  if (!node || node.kind !== config.NODE_TYPE.NODE) return false;
  if (node.hidden || node.deleted || node.isProxy) return false;
  // Donors/surrogates are off-to-the-side satellites, not lineal individuals, so
  // they are excluded from generation numbering (product decision: unnumbered).
  // Gated on the flag so non-repro numbering is byte-identical. NOTE: pregnancies
  // / fetuses remain numbered here (unchanged) — that treatment is a separate open
  // question pending QA, deliberately not altered.
  if (config.ENABLE_REPRO_NODES && isReproSatelliteNode(node)) return false;
  return true;
}

/**
 * Derive standard nomenclature from the current layout nodes.
 *
 * @param {Array<object>} nodes  layout nodes (pedigree().nodes). Each carries
 *        pid, kind, drawn x/y, and virtualY (generation index, 0 = top).
 * @param {object} [opts]
 * @param {(node)=>boolean} [opts.includePredicate]  override which nodes count.
 * @returns {{
 *   byPid: Map<pid,{generation:string, index:number, genIndex:number}>,
 *   generations: Array<{genIndex:number, roman:string, virtualY:number, bandY:number}>
 * }}
 */
export function assignNomenclature(nodes, opts = {}) {
  const include = opts.includePredicate || defaultIncludePredicate;

  const people = (nodes || []).filter((n) => include(n));
  const byPid = new Map();
  if (people.length === 0) return { byPid, generations: [] };

  // Group by generation (virtualY; 0 = topmost).
  const groups = new Map(); // virtualY -> node[]
  for (const n of people) {
    const g = n.virtualY ?? 0;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(n);
  }

  // Roman labels are relative to the topmost PRESENT generation, so the first
  // rendered row is always I even when virtualY/rowDepth doesn't start at 0
  // (disconnected components can shift it). genIndex is 1-based and gap-free
  // over present rows even if virtualY itself has gaps.
  const presentYs = [...groups.keys()].sort((a, b) => a - b);
  const minVirtualY = presentYs[0];

  const generations = [];
  presentYs.forEach((vy, gi) => {
    const genIndex = gi + 1;
    const roman = toRoman(vy - minVirtualY + 1);
    const row = groups.get(vy);

    // Left-to-right by DRAWN x (reflects custom drag positions, so numbers
    // reassign on move), pid tiebreak for float ties / identical x.
    row.sort((a, b) => a.x - b.x || cmpPid(a.pid, b.pid));
    row.forEach((n, i) => {
      byPid.set(n.pid, { generation: roman, index: i + 1, genIndex });
    });

    // Representative band y for the gutter numeral: nodes in a generation share
    // y unless a custom drag moved one, so use the median to stay aligned with
    // the bulk of the row.
    const ys = row.map((n) => n.y).sort((a, b) => a - b);
    const bandY = ys[Math.floor(ys.length / 2)];

    generations.push({ genIndex, roman, virtualY: vy, bandY });
  });

  return { byPid, generations };
}
